import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  canWrite,
  errorMessage,
  financeGet,
  financeSend,
  isManager,
  num,
  useFinanceCodes,
  won,
  type FinanceCtx,
} from '../financeApi';

type BudgetItem = { type: '수입' | '지출'; department: string; category: string; amount: number };
type BudgetYear = { year: number; status: 'draft' | 'confirmed'; confirmedAt: string | null; items: BudgetItem[] };
type Execution = BudgetYear & { actuals: { type: '수입' | '지출'; department: string; category: string; total: number }[] };

const cellKey = (type: string, department: string, category: string) => `${type}|${department}|${category}`;

// 연간 예산 편성: 지출은 부서 × 계정과목, 수입은 헌금 항목별 목표. 재정부원이 편성하고 재정부장이 확정한다.
export default function AnnualBudget({ ctx }: { ctx: FinanceCtx }) {
  const { incomeCategories, expenseCategories, departments } = useFinanceCodes(ctx);
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [tab, setTab] = useState<'지출' | '수입'>('지출');
  const [meta, setMeta] = useState<Pick<BudgetYear, 'status' | 'confirmedAt'> | null>(null);
  const [cells, setCells] = useState<Record<string, number>>({});
  const [extraKeys, setExtraKeys] = useState<BudgetItem[]>([]);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setError(null);
    setNotice(null);
    financeGet<BudgetYear>(ctx, '/annual-budgets', { year })
      .then((data) => {
        setMeta({ status: data.status, confirmedAt: data.confirmedAt });
        setCells(Object.fromEntries(data.items.map((i) => [cellKey(i.type, i.department, i.category), i.amount])));
        setExtraKeys(data.items);
        setDirty(false);
      })
      .catch((err) => setError(errorMessage(err, '예산을 불러오지 못했습니다.')));
  }, [ctx, year]);

  useEffect(load, [load]);

  const locked = meta?.status === 'confirmed' || !canWrite(ctx);

  // 설정에서 지웠거나 이름이 바뀐 옛 항목에 예산이 남아 있으면 표에서 사라지지 않게 행/열에 붙인다.
  const deptRows = useMemo(() => {
    const extra = extraKeys.filter((i) => i.type === '지출').map((i) => i.department);
    return ['', ...departments, ...extra.filter((d) => d && !departments.includes(d))].filter((d, i, a) => a.indexOf(d) === i);
  }, [departments, extraKeys]);
  const expenseCols = useMemo(() => {
    const extra = extraKeys.filter((i) => i.type === '지출').map((i) => i.category);
    return [...expenseCategories, ...extra.filter((c) => !expenseCategories.includes(c))].filter((c, i, a) => a.indexOf(c) === i);
  }, [expenseCategories, extraKeys]);
  const incomeRows = useMemo(() => {
    const extra = extraKeys.filter((i) => i.type === '수입').map((i) => i.category);
    return [...incomeCategories, ...extra.filter((c) => !incomeCategories.includes(c))].filter((c, i, a) => a.indexOf(c) === i);
  }, [incomeCategories, extraKeys]);

  const value = (type: string, department: string, category: string) => cells[cellKey(type, department, category)] ?? 0;
  const setValue = (type: string, department: string, category: string, raw: string) => {
    const amount = Number(raw.replace(/[^\d]/g, '')) || 0;
    setCells((prev) => ({ ...prev, [cellKey(type, department, category)]: amount }));
    setDirty(true);
  };

  const totals = useMemo(() => {
    let expense = 0;
    let income = 0;
    Object.entries(cells).forEach(([key, amount]) => {
      if (key.startsWith('지출|')) expense += amount;
      else income += amount;
    });
    return { expense, income };
  }, [cells]);

  const save = () => {
    setSaving(true);
    setError(null);
    const items = Object.entries(cells)
      .filter(([, amount]) => amount > 0)
      .map(([key, amount]) => {
        const [type, department, category] = key.split('|');
        return { type, department, category, amount };
      });
    financeSend(ctx, 'put', '/annual-budgets', { year, items })
      .then(() => {
        setNotice('예산을 저장했습니다.');
        load();
      })
      .catch((err) => setError(errorMessage(err, '예산을 저장하지 못했습니다.')))
      .finally(() => setSaving(false));
  };

  const setConfirm = (confirm: boolean) => {
    if (confirm && dirty) {
      setError('저장하지 않은 변경이 있습니다. 먼저 저장해 주세요.');
      return;
    }
    const message = confirm
      ? `${year}년 예산을 확정할까요? 확정 후에는 확정을 취소하기 전까지 수정할 수 없습니다.`
      : `${year}년 예산 확정을 취소하고 다시 편성할 수 있게 할까요?`;
    if (!window.confirm(message)) return;
    financeSend(ctx, 'post', '/annual-budgets/confirm', { year, confirm })
      .then(load)
      .catch((err) => setError(errorMessage(err, '처리하지 못했습니다.')));
  };

  // 전년도 예산 또는 전년도 실적을 불러와 채운다(저장 전까지는 화면에만 반영).
  const fillFromPrevious = (mode: 'budget' | 'actual') => {
    if (Object.values(cells).some((v) => v > 0) && !window.confirm('지금 입력된 금액을 덮어씁니다. 계속할까요?')) return;
    financeGet<Execution>(ctx, '/budget-execution', { year: year - 1 })
      .then((data) => {
        const source = mode === 'budget'
          ? data.items.map((i) => ({ ...i }))
          : data.actuals.map((a) => ({ type: a.type, department: a.department, category: a.category, amount: a.total }));
        if (source.length === 0) {
          setNotice(`${year - 1}년 ${mode === 'budget' ? '예산' : '실적'}이 없습니다.`);
          return;
        }
        // 실적은 천 원 단위로 반올림
        setCells(Object.fromEntries(source.map((i) => [
          cellKey(i.type, i.department, i.category),
          mode === 'actual' ? Math.round(i.amount / 1000) * 1000 : i.amount,
        ])));
        setExtraKeys(source);
        setDirty(true);
        setNotice(`${year - 1}년 ${mode === 'budget' ? '예산' : '실적'}을 불러왔습니다. 확인 후 저장해 주세요.`);
      })
      .catch((err) => setError(errorMessage(err, '전년도 자료를 불러오지 못했습니다.')));
  };

  const input = (type: string, department: string, category: string) => (
    <input
      className="fin-cell-input fin-num"
      inputMode="numeric"
      disabled={locked}
      value={value(type, department, category) ? num(value(type, department, category)) : ''}
      placeholder="0"
      onChange={(e) => setValue(type, department, category, e.target.value)}
    />
  );

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">연간 예산 편성</h2>
          <p className="manage-panel-desc">부서별·계정과목별 지출 예산과 헌금 항목별 수입 목표를 편성합니다. 재정부장이 확정하면 수정이 잠깁니다.</p>
        </div>
        <select className="fin-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {Array.from({ length: 6 }, (_, i) => thisYear + 1 - i).map((y) => <option key={y} value={y}>{y}년</option>)}
        </select>
      </div>

      <div className="manage-filterbar">
        <div className="manage-seg">
          <button type="button" className={`manage-seg-btn${tab === '지출' ? ' is-active' : ''}`} onClick={() => setTab('지출')}>지출 예산</button>
          <button type="button" className={`manage-seg-btn${tab === '수입' ? ' is-active' : ''}`} onClick={() => setTab('수입')}>수입 목표</button>
        </div>
        {meta && (
          <span className={`manage-badge ${meta.status === 'confirmed' ? 'manage-badge--green' : 'manage-badge--amber'}`}>
            {meta.status === 'confirmed' ? `확정됨 ${meta.confirmedAt?.slice(0, 10) ?? ''}` : '편성 중 (미확정)'}
          </span>
        )}
        <span className="fin-muted">지출 예산 {won(totals.expense)} · 수입 목표 {won(totals.income)}</span>
        <span className="fin-spacer" />
        {!locked && (
          <>
            <button type="button" className="manage-btn manage-btn--sm" onClick={() => fillFromPrevious('budget')}>전년도 예산 복사</button>
            <button type="button" className="manage-btn manage-btn--sm" onClick={() => fillFromPrevious('actual')}>전년도 실적으로 채우기</button>
          </>
        )}
      </div>

      {error && <div className="fin-inline-error">{error}</div>}
      {notice && <div className="fin-inline-ok">{notice}</div>}

      {tab === '지출' ? (
        <div className="manage-card-block">
          <div className="manage-table-wrap">
            <table className="manage-table fin-grid-table">
              <thead>
                <tr>
                  <th className="fin-sticky">부서 \ 계정과목</th>
                  <th className="fin-num">부서 합계</th>
                  {expenseCols.map((c) => <th key={c} className="fin-num">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {deptRows.map((d) => (
                  <tr key={d || '공통'}>
                    <th className="fin-sticky">{d || '공통'}</th>
                    <td className="fin-num"><b>{num(expenseCols.reduce((s, c) => s + value('지출', d, c), 0))}</b></td>
                    {expenseCols.map((c) => <td key={c}>{input('지출', d, c)}</td>)}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th className="fin-sticky">계정 합계</th>
                  <td className="fin-num"><b>{num(totals.expense)}</b></td>
                  {expenseCols.map((c) => (
                    <td key={c} className="fin-num"><b>{num(deptRows.reduce((s, d) => s + value('지출', d, c), 0))}</b></td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <div className="manage-card-block">
          <div className="manage-table-wrap">
            <table className="manage-table">
              <thead>
                <tr><th>헌금·수입 항목</th><th className="fin-num" style={{ width: 200 }}>연간 목표</th><th className="fin-num" style={{ width: 160 }}>월 평균</th></tr>
              </thead>
              <tbody>
                {incomeRows.map((c) => (
                  <tr key={c}>
                    <td><b>{c}</b></td>
                    <td>{input('수입', '', c)}</td>
                    <td className="fin-num fin-muted">{num(Math.round(value('수입', '', c) / 12))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr><th>합계</th><td className="fin-num"><b>{num(totals.income)}</b></td><td className="fin-num">{num(Math.round(totals.income / 12))}</td></tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <div className="fin-actions">
        {isManager(ctx) && meta?.status === 'confirmed' && (
          <button type="button" className="manage-btn" onClick={() => setConfirm(false)}>확정 취소</button>
        )}
        {!locked && (
          <button type="button" className="manage-btn manage-btn--primary" onClick={save} disabled={saving || !dirty}>
            {saving ? '저장 중...' : dirty ? '예산 저장' : '저장됨'}
          </button>
        )}
        {isManager(ctx) && meta?.status !== 'confirmed' && (
          <button type="button" className="manage-btn manage-btn--primary" onClick={() => setConfirm(true)}>예산 확정</button>
        )}
      </div>
      {!isManager(ctx) && meta?.status !== 'confirmed' && canWrite(ctx) && (
        <p className="fin-muted fin-right">예산 확정은 재정부장이 합니다.</p>
      )}
    </div>
  );
}
